import { WordCorrection } from '@app/routes/content/routes/recordings/domain';
import {
  selectActiveAdvancedEdit,
  selectActiveEditWordCorrections,
  selectFilteredTranscriptSegments,
  selectRecording,
} from '@app/routes/content/routes/recordings/selector';
import { useAppSelector } from '@app/store/store';
import { useMemo } from 'react';

import { type FlatWord, buildFlatWords } from '../utils/build-flat-words';
import {
  type EffectiveWordGroup,
  buildEffectiveGroups,
  buildEffectiveText,
  buildEffectiveVtt,
} from '../utils/effective-transcript';

export type UseEffectiveTranscriptResult = {
  /**
   * The flat word list used by the word-level renderer and selection logic.
   * `isCut` and `correction` are pre-computed per word.
   */
  flatWords: FlatWord[];
  /**
   * Consecutive non-cut word groups, each mapping to one VTT cue / text line.
   * A cut inside a segment produces two separate groups from that segment.
   */
  effectiveGroups: EffectiveWordGroup[];
  /** Full WebVTT string with remapped edit-timeline timestamps. */
  vttContent: string;
  /**
   * Plain text — one effective group per line, no timestamps.
   * Used for both the Copy button and the TXT download.
   */
  textContent: string;
  /** Edit title — used as the base filename for downloads. */
  editTitle: string;
  /**
   * Raw word corrections array — needed by InlineWordEditor to upsert/remove
   * individual corrections without losing the others.
   */
  wordCorrections: WordCorrection[];
};

/**
 * Shared hook for the EditTranscript component tree.
 *
 * Reads transcript segments, active edit cuts, and word corrections from Redux,
 * then memoises:
 *   flatWords       → word-level rendering list (isCut, correction pre-computed)
 *   effectiveGroups → segment-aware groups of non-cut corrected words
 *   vttContent      → WebVTT string with remapped timestamps
 *   textContent     → plain text (one group per line) for Copy + TXT download
 *
 * Consumed by:
 *   - index.tsx      → flatWords for the virtualized renderer
 *   - header-section → vttContent, textContent, editTitle for export buttons
 */
export const useEffectiveTranscript = (): UseEffectiveTranscriptResult => {
  const segments = useAppSelector(
    selectFilteredTranscriptSegments,
  ) as ReturnType<typeof selectFilteredTranscriptSegments>;

  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const wordCorrections = useAppSelector(selectActiveEditWordCorrections);
  const recording = useAppSelector(selectRecording);

  const cuts = useMemo(() => {
    return activeEdit?.draft.cuts ?? [];
  }, [activeEdit?.draft.cuts]);

  const flatWords = useMemo(
    () => buildFlatWords(segments, cuts, wordCorrections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segments, cuts, wordCorrections],
  );

  const effectiveGroups = useMemo(
    () => buildEffectiveGroups(flatWords, segments, cuts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatWords, segments, cuts],
  );

  const vttContent = useMemo(
    () => buildEffectiveVtt(effectiveGroups),
    [effectiveGroups],
  );

  const textContent = useMemo(
    () => buildEffectiveText(effectiveGroups),
    [effectiveGroups],
  );

  // Use edit title if available, fall back to recording title, then a generic name
  const editTitle = activeEdit?.draft.title || recording?.title || 'transcript';

  return {
    flatWords,
    effectiveGroups,
    vttContent,
    textContent,
    editTitle,
    wordCorrections,
  };
};
